// What the theme preview is told to show (R806).
//
// `PreviewLook` is the preview's own input. Its fields carry the names of the
// theme bodies (themes.md §3): the World body, the Marks body, the Chart
// body and the UI body. The look resolver of the client adapts what it
// resolves to this shape, and the MODDING card and the theme workshop hand
// it to `ThemePreview.show`.
//
// A field that names a look the client does not draw yet is optional and
// carries no default here. The preview draws the AUTO look whatever such a
// field holds (R822).

import { DEFAULT_SETTINGS } from '../ui/settings';
import type {
  ChartSetting, DamageStyle, HoldSymbol, SpawnStyle, TorpedoStyle,
} from '../ui/settings';
import type { ChartPieceOverlay } from '../fast/chart/furniture';
import { FALLBACK_FACTION, themeBody, type ThemeBody } from '../ui/theme';
import { defaultLayerView, type LayerView } from '../ui/layerview';
import { VENDORED_THEMES } from '../gen/presentation';

/** The vendored AUTO World and Marks themes (themes.md §4.1). */
const AUTO_WORLD = VENDORED_THEMES.world[0];
const AUTO_MARKS = VENDORED_THEMES.marks[0];

/**
 * What the selected row changes. `show` moves the camera to that thing and
 * plays it on a loop. The order is the order of the theme bodies.
 */
export const PREVIEW_FOCI = [
  'territory', 'terrain',
  // The current field (R2716). The focus frames the open water, where the
  // staged current runs, and only this focus lays the field on the board.
  'currents',
  // The current heat look (R2869). The focus frames the open water, lays
  // the field on the board and draws the heat look, as the CURRENTS heat
  // map does: with no depth tints.
  'currentsHeat',
  // The depth look (R2726). The focus frames the staged band field, and
  // only this focus lays the field on the board.
  'depth',
  // The depth look of the light identity (R3313): the same frame, and the
  // row draws the look of paper.
  'depthLight',
  // The four faction marks (R1568). Each focus frames the staged rival:
  // its hold, its contact, its border, and the key in the corner.
  'factionSite', 'factionHull', 'factionEdge', 'factionEdgeLight', 'factionKey',
  'spawn', 'torpedo',
  // The detonation look (R2079). The focus runs the staged rounds of three
  // kinds into the staged target, one after the other, so the look shows
  // three splashes.
  'detonation',
  'damage', 'beam', 'aura',
  // The six gun looks (R1007). Each one runs a fight on the staged pair: the
  // three shot foci fly that family's salvo, and the three hit foci land it.
  'shotMelee', 'shotArtillery', 'shotSentinel',
  'hitShield', 'hitHull', 'hitSink',
  // The hold-fire look (R1439). The focus frames the staged own Sentinel,
  // which holds fire.
  'holdFire',
  // The tube reload look (R1474). The focus frames the staged own Lancer,
  // whose tubes fire, reload and wait.
  'reload',
  // The Dredger harvesting look (R1120). The focus runs the staged Dredger
  // over the staged deposit and holds the cut.
  'gather',
  // The anchoring look (R1125). The focus frames the staged own Beacon,
  // whose cable runs the cycle, beside the set Tender.
  'anchoring',
  // The Sapper part (R2652, R2657). The two mark foci frame the staged sea
  // mines and deployables round the staged Sapper; the five look foci run
  // a lay, a take or a detonation on a loop.
  'seaMine', 'deployable', 'seaMineDetonation', 'seaMineLay', 'deployableLay',
  'seaMineCollect', 'deployableCollect',
  // The cache work look (R2833). The focus frames the staged own Vedette,
  // which works a SEARCH on a loop.
  'cacheWork',
  'notices', 'dock', 'banner',
  'ship', 'ownFleet', 'squadFleet', 'shield', 'digits', 'hold', 'structure',
  'ownHold', 'squadHold', 'deposit',
  // The picture of a MARKS option that changes both the hull marks and the
  // hold marks (R1915). The focus frames the own hold and the two own hulls
  // beside it, the Sentinel and the set Tender.
  'holdHulls',
  // The map marker look (R1291). The focus frames the staged markers: the
  // four kinds for the faction, and the attack kind at three ages for the
  // squad, as the cards of round 062 drew them.
  'mapMarker',
  // The point of interest marks (R2714). The focus frames the five staged
  // points, one of each kind, round the trench.
  'poi',
  'chart', 'ui',
  // The three effect slots (effects.md §3, R908). They are no theme body:
  // a player picks an effect in the EFFECTS card of MODDING, and the
  // preview shows the pick on the staged fleets, the round and the hold.
  'effectFleet', 'effectTorpedo', 'effectHold',
] as const;
export type PreviewFocus = (typeof PREVIEW_FOCI)[number];

/** The foci the chrome shows. The preview runs the real DOM module for them. */
export const DOM_FOCI: ReadonlySet<PreviewFocus> = new Set<PreviewFocus>([
  'notices', 'dock', 'banner', 'ui',
]);

/** How the territory starts: the sovereignty field, or the hex view. */
export type TerritoryView = 'default' | 'hex';

export interface PreviewLook {
  // -- the World body -----------------------------------------------------
  /** Which layers the territory starts with (owner ruling 3). */
  territory: TerritoryView;
  /** The terrain. One look is drawn, and it is on. */
  terrain?: string;
  /** The current field (R2716). One look is drawn, on the `currents` row
   *  alone. */
  currents?: string;
  /** The current heat look (R2869). One look is drawn, on the
   *  `currentsHeat` row alone. */
  currentsHeat?: string;
  /** The depth look (R2726). One look is drawn, on the `depth` row alone. */
  depth?: string;
  /** The depth look of the light identity (R3313). One look is drawn, on the
   *  `depthLight` row alone, or on every row when the page is in the light
   *  identity. */
  depthLight?: string;
  /** The four faction marks (R1568): the charge beside a rival hold, the
   *  pip astern of a rival hull, the dash of a border and the key. */
  factionSite?: string;
  factionHull?: string;
  factionEdge?: string;
  /** The border look of the light identity (R3313). */
  factionEdgeLight?: string;
  factionKey?: string;
  /** The arrival on the map. */
  spawn: SpawnStyle;
  /** The round style (R164). */
  torpedo: TorpedoStyle;
  /** The damage figure (R538). */
  damage: DamageStyle;
  /** The Tender heal beam (R723). One look is drawn. */
  beam?: string;
  /** The Tender shield aura (R723). One look is drawn. */
  aura?: string;
  /** The notices and the drain line (R430). One look is drawn. */
  notices?: string;
  /** The chrome dock-in (R182). One look is drawn. */
  dock?: string;
  /** The season banner (R184). One look is drawn. */
  banner?: string;
  /** The rounds one salvo flies, by the ROLE of the shooter (R1007). One look
   *  is drawn per field. */
  shotMelee?: string;
  shotArtillery?: string;
  shotSentinel?: string;
  /** What one round draws when it arrives (R1007). */
  hitShield?: string;
  hitHull?: string;
  hitSink?: string;
  /** What an own fleet draws around its hull while it holds fire (R1439).
   *  One look is drawn. */
  holdFire?: string;
  /** What an own hull with tubes draws for its tube reload (R1474). One
   *  look is drawn. */
  reload?: string;
  /** What a round draws where it goes off (R2079). One look is drawn. */
  detonation?: string;
  /** What a Dredger draws while it cuts a deposit (R1120). One look is
   *  drawn. */
  gather?: string;
  /** What a hull draws while its anchor goes down, holds and comes up
   *  (R1125). One look is drawn. */
  anchoring?: string;
  /** The five World fields of the Sapper part (R2652): the detonation of
   *  a sea mine, the two lay looks and the two collect looks. */
  seaMineDetonation?: string;
  seaMineLay?: string;
  deployableLay?: string;
  seaMineCollect?: string;
  deployableCollect?: string;
  /** What an own fleet draws while it works a SEARCH
   *  (R2833). One look is drawn. */
  cacheWork?: string;

  // -- the Marks body -----------------------------------------------------
  /** The ship icon set the whole roster wears (R835). One look is drawn. */
  ship?: string;
  /** The fourteen hull fields (R835, R2550, R2652). `follow` takes the set.
   *  A hull row of the workshop focuses `ship`, so none of the fourteen is a
   *  focus. */
  shipCorsair?: string;
  shipCutlass?: string;
  shipFalconet?: string;
  shipBasilisk?: string;
  shipBombard?: string;
  shipLancer?: string;
  shipCommodore?: string;
  shipSentinel?: string;
  shipVedette?: string;
  shipOracle?: string;
  shipTender?: string;
  shipBeacon?: string;
  shipDredger?: string;
  shipSapper?: string;
  /** The mark on an own hull (R185). One look is drawn. */
  ownFleet?: string;
  /** The mark on a squadmate hull (R185). One look is drawn. */
  squadFleet?: string;
  /** The shield bars. One look is drawn. */
  shield?: string;
  /** The group badge (R427). One look is drawn. */
  digits?: string;
  /** The hold symbol (R633). */
  hold: HoldSymbol;
  /** The set all nine strategic objects are drawn as (R1103). ONE field
   *  names the whole set, so the structures take no fold. One look is
   *  drawn. */
  structure?: string;
  /** The mark on an own hold (R185). One look is drawn. */
  ownHold?: string;
  /** The mark on a squadmate hold (R185). One look is drawn. */
  squadHold?: string;
  /** The deposit mark (R475). One look is drawn. */
  deposit?: string;
  /** The map marker look (R1291). The preview stages the four kinds and
   *  the three ages of mockup round 062 in it. One look is drawn. */
  mapMarker?: string;
  /** The sea mine and the deployable marks (R2652). One look is drawn. */
  seaMine?: string;
  deployable?: string;
  /** The point of interest marks (R2714). The preview stages one point of
   *  each kind in the set. One set is drawn. */
  poi?: string;

  // -- the Chart body -----------------------------------------------------
  /** The reading of the chart furniture. It is a DISPLAY setting, not a
   *  theme, but the preview must know it to draw the pieces. */
  chart: ChartSetting;
  /** The Chart theme's word over the pack, per piece and per reading. */
  chartPieces?: ChartPieceOverlay | null;

  // -- the UI body --------------------------------------------------------
  /** The chrome tokens the frame wears. */
  ui: ThemeBody;

  // -- the effects --------------------------------------------------------
  /** The effects the staged objects wear (R908, R910). A look with no
   *  entry here shows no effect at all. */
  effects?: PreviewEffects;
}

/** The catalog wire ids of the three effect slots the preview draws, as a
 *  state frame carries them (R910). 0 is no effect. */
export interface PreviewEffects {
  fleet: number;
  torpedo: number;
  hold: number;
}

/** Today's look: the vendored AUTO themes and faction 1's chrome. */
export function defaultPreviewLook(): PreviewLook {
  return {
    territory: 'default',
    spawn: AUTO_WORLD.spawn,
    torpedo: AUTO_WORLD.torpedo,
    damage: AUTO_WORLD.damage,
    hold: AUTO_MARKS.hold,
    chart: DEFAULT_SETTINGS.chart,
    chartPieces: null,
    ui: themeBody(FALLBACK_FACTION),
  };
}

/**
 * The layer view a territory start state gives (owner ruling 3).
 *
 * `default` is the shipped picture: the sovereignty fills and borders on,
 * the hex views off. `hex` turns the hex fills and borders on and the two
 * sovereignty terms off, so the reader sees the cells a faction holds and
 * nothing drawn over them. The terrain is on in both. The keys j, k, l, [
 * and ] still flip each layer for one session in the game.
 */
export function territoryLayerView(t: TerritoryView): LayerView {
  if (t !== 'hex') return defaultLayerView();
  return { sovFills: false, sovBorders: false, terrain: true, hexFills: true, hexBorders: true };
}
